import { uniqueId } from 'lodash'
import React, { FC, useMemo } from 'react'
import { format, nowDate, secToHourMinute } from '../../../utils/date'
import LoadingSquare from '../common/LoadingSquare'
import Tips from '../common/Tips'

interface Props {
  list: {
    label: string
    sales: number
    member_cost: number
    other_cost: number
  }[]
  chartSize: number
  type?: string
  gap?: boolean
  ruler?: boolean
  scale?: boolean
  labelSkip?: number
  vLabelLimit?: number
  tips?: boolean
  clickCallback: (label: string) => void
  selectLabel?: string
  max: number
  isGraphLoading: boolean
}

const DetailCostChart: FC<Props> = (props) => {
  const {
    list,
    chartSize,
    type,
    gap,
    ruler,
    scale,
    labelSkip,
    vLabelLimit,
    tips,
    clickCallback,
    selectLabel,
    max,
    isGraphLoading,
  } = props

  const V_LABEL_LIMIT = 3

  const LEGEND_LIST = [
    {
      name: "売上・予算",
      color: "#FFFFFF",
    },
    {
      name: "その他のコスト",
      color: "#ececec",
    },
    {
      name: "業務コスト",
      color: "#ecfce9",
    },
  ]

  const maxValue = useMemo(() => {
    const tmpMax = Math.max(...list.map((v) => [v.sales, v.other_cost + v.member_cost]).flat(), max)
    const tmpAry = tmpMax.toString().split('')
    if (tmpAry.length < 3) {
      return 100
    }
    if (tmpMax % Number(`1${"0".repeat(tmpAry.length - 1)}`) === 0) {
      return tmpMax
    }
    if (tmpAry[0] === "9") {
      return Number(`1${"0".repeat(tmpAry.length)}`)
    }
    if (["5", "6", "7", "8", "9"].includes(tmpAry[1])) {
      return Number(`${(Number(tmpAry[0]) + 1).toString()}${"0".repeat(tmpAry.length - 1)}`)
    }
    if (["0", "1", "2", "3", "4"].includes(tmpAry[1])) {
      return Number(`${(Number(tmpAry[0])).toString()}5${"0".repeat(tmpAry.length - 2)}`)
    }
    return Number(`${tmpAry[0]}${(Number(tmpAry[1]) + 1).toString()}${"0".repeat(tmpAry.length - 2)}`)
  }, [list])

  const buildUnit = (value: number) => {
    if (!value) return <></>
    if (type === "person") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          人
        </>
      )
    }
    if (type === "biz") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          biz
        </>
      )
    }
    if (type === "page") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          枚
        </>
      )
    }
    if (type === "time") {
      return (
        <span>
          {secToHourMinute(value)}
        </span>
      )
    }
    if (type === "times") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          回
        </>
      )
    }
    if (type === "percent") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          %
        </>
      )
    }
    if (type === "hour") {
      return (
        <>
          <span>
            {Math.floor(value / 3600)}
          </span>
          時間
        </>
      )
    }
    if (type === "yen") {
      return (
        <>
          ¥
          <span>
            {value.toLocaleString()}
          </span>
        </>
      )
    }
    return (
      <></>
    )
  }

  const buildLabel = (label: string) => {
    const text = format(label, "YYYYMM") === nowDate("YYYYMM") ? nowDate('YYYY年MM月DD日現在') : format(label, 'YYYY年MM月')
    return (
      <span>
        {text}
      </span>
    )
  }

  const buildVerticalBarChart = () => (
    <>
      {
        list.map((value, index) => (
          <div
            className="column_value"
            style={{
              height: `${chartSize}px`,
              width: `calc(100% / ${list.length || 1})`,
            }}
            key={uniqueId('column_value_')}
          >
            <div
              className="stacked_bar_chart"
              style={{
                width: "50%",
                height: `${(Math.max(value.sales, value.member_cost + value.other_cost) / (maxValue * 1.03)) * 100}%`,
                backgroundColor: "#4572ba",
              }}
            >
              {
                tips ? (
                  <Tips
                    description={`売上: ¥${value.sales.toLocaleString()}`}
                    className="abs"
                    icon={false}
                  >
                    <></>
                  </Tips>
                ) : (
                  <></>
                )
              }
              {
                value.sales < value.member_cost + value.other_cost && (
                  <>
                    <div
                      className="bar"
                      style={{
                        height: `calc(${(
                          ((value.member_cost + value.other_cost) - value.sales) / Math.max(value.sales, value.member_cost + value.other_cost)
                        ) * 100}% - ${value.sales ? "6px" : "0px"})`,
                        backgroundImage: `linear-gradient(
                          -45deg, #F9A7D4 25%, #FFFFFF 25%, #FFFFFF 50%, #F9A7D4 50%, #F9A7D4 75%, #FFFFFF 75%)`,
                        backgroundSize: "10px 10px",
                      }}
                    />
                  </>
                )
              }
              <div
                className="bar"
                style={{
                  height: `calc(${(value.sales / Math.max(value.sales, value.member_cost + value.other_cost)) * 100}%
                  - ${value.sales < (value.member_cost + value.other_cost) ? "0px" : "6px"})`,
                  width: "calc(100% - 6px)",
                  backgroundColor: "#FFFFFF",
                  margin: "3px",
                }}
              >
                {
                  value.sales > value.member_cost + value.other_cost && (
                    <>
                      <div
                        className="bar"
                        style={{
                          height: `${(
                            (value.sales - (value.member_cost + value.other_cost))
                            / Math.max(value.sales, value.member_cost + value.other_cost)
                          ) * 100}%`,
                          backgroundImage: `linear-gradient(
                            -45deg, #A8BADB 25%, #FFFFFF 25%, #FFFFFF 50%, #A8BADB 50%, #A8BADB 75%, #FFFFFF 75%)`,
                          backgroundSize: "10px 10px",
                        }}
                      />
                    </>
                  )
                }
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "-50px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: value.sales >= value.member_cost + value.other_cost ? "#4D71B5" : "#FF4EAE",
                }}
              >
                <div>
                  利益
                </div>
                <div>
                  <span
                    style={{
                      fontSize: "20px",
                    }}
                  >
                    {
                      !!value.sales && !!(value.member_cost + value.other_cost) ? (
                        <>
                          {`¥${(value.sales - (value.member_cost + value.other_cost)).toLocaleString()}`}
                        </>
                      ) : (
                        <>
                          -
                        </>
                      )
                    }
                  </span>
                </div>
              </div>
            </div>
            <div
              className="stacked_bar_chart"
              style={{
                width: "50%",
                height: `${((value.member_cost + value.other_cost) / (maxValue * 1.03)) * 100}%`,
                backgroundColor: "#4572ba",
                marginLeft: "-3px",
              }}
            >
              <div
                className="bar"
                style={{
                  height: `calc(${(value.other_cost / (value.member_cost + value.other_cost)) * 100}% - 6px)`,
                  width: "calc(100% - 6px)",
                  backgroundColor: "#ECECEC",
                  margin: "3px",
                }}
              >
                {
                  tips ? (
                    <Tips
                      description={`その他のコスト: ¥${(value.other_cost).toLocaleString()}`}
                      className=""
                      icon={false}
                    >
                      <></>
                    </Tips>
                  ) : (
                    <></>
                  )
                }
              </div>
              <div
                className="bar"
                style={{
                  height: `calc(${(value.member_cost / (value.member_cost + value.other_cost)) * 100}% - ${value.other_cost ? "3px" : "6px"})`,
                  width: "calc(100% - 6px)",
                  backgroundColor: "#ECFCE9",
                  margin: "3px",
                }}
              >
                {
                  tips ? (
                    <Tips
                      description={`業務コスト: ¥${value.member_cost.toLocaleString()}`}
                      className=""
                      icon={false}
                    >
                      <></>
                    </Tips>
                  ) : (
                    <></>
                  )
                }
              </div>
            </div>
            {
              ruler ? (
                <div className="ruler_border" />
              ) : (
                <></>
              )
            }
            {
              labelSkip && (index % labelSkip) !== 0 ? (
                <></>
              ) : (
                <div
                  className="column_title"
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#4f71b5",
                  }}
                >
                  {buildLabel(value.label)}
                  <button
                    type="button"
                    onClick={() => clickCallback(value.label)}
                  >
                    {selectLabel !== value.label ? "選択" : "選択解除"}
                  </button>
                </div>
              )
            }
            {
              selectLabel === value.label && (
                <div className="selected long" />
              )
            }
          </div>
        ))
      }
    </>
  )

  return (
    <>
      <div className="bar_chart_wrap">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            flexWrap: "wrap",
            width: "100%",
            marginBottom: "80px",
          }}
        >
          {
            LEGEND_LIST.map((v) => (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: v.color,
                      width: "16px",
                      height: "16px",
                      border: "solid 2px #4572ba",
                    }}
                  />
                  <div>
                    {v.name}
                  </div>
                </div>
              </>
            ))
          }
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div
              style={{
                backgroundImage: `linear-gradient(
                  -45deg, #F9A7D4 25%, #FFFFFF 25%, #FFFFFF 50%, #F9A7D4 50%, #F9A7D4 75%, #FFFFFF 75%)`,
                backgroundSize: "10px 10px",
                width: "16px",
                height: "16px",
                border: "solid 1px #CCCCCC",
              }}
            />
            <div
              style={{
                backgroundImage: `linear-gradient(
                  -45deg, #A8BADB 25%, #FFFFFF 25%, #FFFFFF 50%, #A8BADB 50%, #A8BADB 75%, #FFFFFF 75%)`,
                backgroundSize: "10px 10px",
                width: "16px",
                height: "16px",
                border: "solid 1px #CCCCCC",
              }}
            />
            <div>
              利益
            </div>
          </div>
        </div>
        <div className={`vertical ${(ruler && type) ? "ruler" : ""}`}>
          <div
            className="bar_chart_area"
            style={{
              height: `${chartSize}px`,
              justifyContent: gap ? "space-evenly" : "center",
            }}
          >
            {
              isGraphLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexFlow: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 2,
                    textShadow: "1px 1px 1px #ffffff",
                  }}
                >
                  <LoadingSquare>
                    <>
                      表示するデータを生成しています…
                    </>
                  </LoadingSquare>
                </div>
              )
            }
            {
              scale && Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                <div
                  className="scale"
                  style={{
                    top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                  }}
                  key={uniqueId("ruler")}
                />
              ))
            }
            {buildVerticalBarChart()}
          </div>
          {
            ruler ? (
              <div className="ruler_area">
                <div
                  className="horizontal_ruler_area"
                  style={{
                    top: `${chartSize}px`,
                  }}
                />
                {
                  type ? (
                    <div className="vertical_ruler_area">
                      <div className="label_area">
                        {
                          Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                            <React.Fragment key={uniqueId("ruler")}>
                              <div
                                className="scale"
                                style={{
                                  top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                }}
                              />
                              <div
                                className="label"
                                style={{
                                  top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                  fontSize: i ? "14px" : "20px",
                                  color: i ? "" : "#a5a00e",
                                }}
                              >
                                {buildUnit(Math.floor((maxValue / (vLabelLimit || V_LABEL_LIMIT)) * ((vLabelLimit || V_LABEL_LIMIT) - i)))}
                              </div>
                            </React.Fragment>
                          ))
                        }
                      </div>
                    </div>
                  ) : (
                    <></>
                  )
                }
              </div>
            ) : (
              <></>
            )
          }
        </div>
      </div>
    </>
  )
}

DetailCostChart.defaultProps = {
  type: "",
  gap: true,
  ruler: false,
  scale: false,
  labelSkip: 0,
  vLabelLimit: 3,
  tips: false,
  selectLabel: "",
}

export default DetailCostChart
